/* eslint-disable @next/next/no-img-element */
import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BorderBeam } from 'border-beam';
import classNames from 'classnames';
import { useDrop } from 'ahooks';
import MediaInput from '$/components/MediaInput';
import InputPanelToolBar from '$/components/InputPanelToolBar';
import { mergeMyIpInputRoles } from '$/components/InputPanelToolBar/ipSelection';
import InputTextArea from '$/components/InputTextAreaV2';
import type { V3CanvasHomeCategoryInfo } from '$/domains/caseV3/type';
import type { MyIpItem } from '$/domains/myIp/types';
import AssetLibraryModalContent from '@/components/AssetModalContent/AssetLibraryModalContent';
import HistoryModalContent from '@/components/AssetModalContent/HistoryModalContent';
import type { MaterialTypeValue } from '@/components/AssetModalContent/type';
import { useCommonStore } from '@/store/useCommonStore';
import { UseInputComposerResult } from '$/hooks/useInputComposer/types';
import { DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS } from '$/hooks/useInputComposer';
import useCreateRoom, { type AgentTeamRoomProgress, type AgentTeamRoomResult } from '$/hooks/useCreateRoom';
import { rememberMyIpProjectHandoff } from '$/domains/myIp/projectHandoff';
import type { InputPanelAssetTrackLocation, SkillTrackParams, UploadButtonItem } from '$/components/InputPanelToolBar/types';
import { openPreview } from '$/store/usePreviewStore';
import type { PreviewMediaItem } from '$/pages/RoboneoChatBoard/type';
import { mtstatTrack } from '$/utils/mtstat';
import { FILE_TYPES, IMAGE_TYPES, VIDEO_TYPES } from '$/utils/upload';
import { useLanguageStore } from '$/store/languageStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import MyIpDetailModal from '@/components/MyIpDetailModal';
import styles from './index.module.scss';

const HOME_ASSET_LIBRARY_ALLOWED_TYPES: MaterialTypeValue[] = [
  'image', 'video', 'audio', 'document', 'excel', 'ppt',
];

type Props = {
  className?: string;
  inputPlaceholder?: string;
  inputComposer: UseInputComposerResult;
  /** 首页角色所属的账号空间；未提供时保留其他输入入口的原有跳转。 */
  roleCanvasScopeKey?: string;
  selectedPromptGroup?: V3CanvasHomeCategoryInfo | null;
  trackLocation?: string;
  /** 创建房间埋点 location，默认与 trackLocation 一致 */
  createTrackLocation?: string;
  assetTrackLocation?: InputPanelAssetTrackLocation;
  /** 是否展示首页专用的角色、模型选择入口。 */
  showExtendedSelectors?: boolean;
  /** 首页真实 IP 数据源注入的分页选择器，避免工具栏依赖应用层请求。 */
  roleSelectContent?: React.ReactNode;
  /** 展开角色弹框时按需启动首页角色请求。 */
  onRolePopupOpenChange?: (open: boolean) => void;
  renderSubmitButton?: (params: {
    disabled: boolean;
    onClick: () => void;
  }) => React.ReactNode;
};

/**
 * 渲染新首页首屏输入框。
 * 交互逻辑：
 * 1. 上传资源展示在输入框上方预览区（composer.resourceList），不直接进入编辑器内容；
 *    用户可通过 @ 将已上传的资源插入到编辑器中引用。
 * 2. 删除预览区某个资源时，编辑器中对该资源的引用全部删除，重置操作栈。
 * 3. 发送时：文本内容取编辑器 serialized content，媒体资源取全部已上传列表。
 */
function HomeInputPanel({
  className,
  inputPlaceholder,
  inputComposer,
  roleCanvasScopeKey,
  selectedPromptGroup,
  trackLocation = 'home_page',
  createTrackLocation,
  assetTrackLocation = 'home_page',
  showExtendedSelectors = false,
  roleSelectContent,
  onRolePopupOpenChange,
  renderSubmitButton,
}: Props) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const initUserInfo = useCommonStore((state) => state.initUserInfo);
  const { loading, createRoom } = useCreateRoom({
    onLoginSuccessed: async () => {
      await initUserInfo();
    },
  });

  const [isInputPanelActive, setIsInputPanelActive] = useState(false);
  const [resourcePopupType, setResourcePopupType] = useState<'history' | 'assetLibrary'>('history');
  /** 输入预览区点击角色缩略图时，以弹框打开完整 IP 详情。 */
  const [previewIpId, setPreviewIpId] = useState<string>();
  const inputPanelRef = useRef<HTMLDivElement>(null);
  const localUploadInputRef = useRef<HTMLInputElement>(null);
  const isSubmitDisabled = inputComposer.isSubmitDisabled || loading;
  const selectedSkillList = inputComposer.selectedSkillInfoList;
  const shouldShowResourceInput = inputComposer.resourceList.length > 0
    || inputComposer.uploading
    || (!showExtendedSelectors && selectedSkillList.length > 0);
  /** 首页上传菜单点击统一上报 upload_control_click。 */
  const trackUploadControlClick = useCallback((mediaType: string) => {
    mtstatTrack('upload_control_click', {
      location: trackLocation,
      media_type: mediaType,
      trigger_type: 'click',
      thinking_mode: inputComposer.agentThinkingModeType,
    });
  }, [inputComposer.agentThinkingModeType, trackLocation]);

  const localUploadAccept = [
    ...(!inputComposer.imageUploadDisabled ? IMAGE_TYPES : []),
    ...(!inputComposer.videoUploadDisabled ? VIDEO_TYPES : []),
    ...(!inputComposer.documentUploadDisabled ? FILE_TYPES : []),
  ].join(',');

  /** 新版输入面板合并本地资源入口，历史记录和资产库仍打开各自弹框。 */
  const trackedUploadButtonList = useMemo<UploadButtonItem[]>(() => {
    const buttonList = inputComposer.uploadButtonList.filter((item) => item.key === 'asset').map((item) => ({
      ...item,
      onClick: () => {
        if (item.key === 'asset') setResourcePopupType('history');
        trackUploadControlClick(item.key);
        item.onClick();
      },
    }));
    const historyButton = buttonList.find((item) => item.key === 'asset');
    return [
      {
        key: 'local',
        icon: 'uploadBold',
        text: languageStore.smartcatTranslationText((v) => v.roboneo_web_local_upload),
        disabled: inputComposer.uploading || !localUploadAccept,
        onClick: () => localUploadInputRef.current?.click(),
      },
      ...buttonList,
      {
        key: 'assetLibrary',
        icon: 'folderOpenBold',
        text: languageStore.smartcatTranslationText((v) => v.upload_from_asset_library),
        disabled: historyButton?.disabled ?? true,
        onClick: () => {
          setResourcePopupType('assetLibrary');
          trackUploadControlClick('assetLibrary');
          mtstatTrack('roboneo_asset_upload_click_new', {
            location: 'home_page',
            room_id: '',
          });
          inputComposer.actions.openAssetLibrary();
        },
      },
    ];
  }, [
    inputComposer.actions,
    inputComposer.uploadButtonList,
    inputComposer.uploading,
    languageStore,
    localUploadAccept,
    trackUploadControlClick,
  ]);
  /** 清空选择值以便重复选择同一文件，复用 composer 的混合上传和分类限额。 */
  const handleLocalUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const files = Array.from(input.files || []);
    input.value = '';
    if (inputComposer.uploading || !localUploadAccept) return;
    inputComposer.actions.uploadDroppedFiles(files, 'click');
  };

  const assetLibraryAddLabel = languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_studio_tool_add);
  const remainingImageCount = Math.max(0, DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS.maxImageNum - inputComposer.imageCount);
  const remainingVideoCount = Math.max(0, DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS.maxVideoNum - inputComposer.videos.length);
  const remainingDocumentCount = Math.max(0, DEFAULT_INPUT_COMPOSER_UPLOAD_LIMITS.maxDocumentNum - inputComposer.documents.length);
  const assetLibraryMaxSelectableCount = remainingImageCount + remainingVideoCount + remainingDocumentCount;

  /** 资产库角色 IP 复用输入框角色资源模型，添加后回到输入预览区。 */
  const handleIpItemsAdd = useCallback((items: MyIpItem[]) => {
    inputComposer.actions.setSelectedRoles(mergeMyIpInputRoles(inputComposer.selectedRoleInfoList, items));
    inputComposer.actions.closeAssetLibrary();
  }, [inputComposer.actions, inputComposer.selectedRoleInfoList]);

  /** 监听拖拽/粘贴上传，上传后的资源进入新版输入框预览区。 */
  useDrop(inputPanelRef, {
    onFiles(files: File[], event?: React.DragEvent) {
      inputComposer.actions.uploadDroppedFiles(files, event?.type);
    },
  });

  // ─── 输入框光效激活态 ──────────────────────────────────────────────────
  const handleInputFocus = useCallback(() => {
    setIsInputPanelActive(true);
  }, []);

  // ─── 输入框光效离开态 ──────────────────────────────────────────────────
  const handleInputBlur = useCallback(() => {
    setIsInputPanelActive(false);
  }, []);

  /**
   * 预览区点击：角色 IP 以弹框打开完整详情页内容，图片/视频仍打开媒体预览。
   * 角色即使无头像也可凭 ipId 打开详情，不再依赖大图预览所需的 url。
   */
  const handlePreviewResource = useCallback((resource: UseInputComposerResult['resourceList'][number]) => {
    if (resource.mediaType === 'input_ip') {
      if (!resource.ipId) return;
      setPreviewIpId(resource.ipId);
      return;
    }
    const previewResources = inputComposer.resourceList.filter(
      (item) => item.url && (item.mediaType === 'image' || item.mediaType === 'video'),
    );
    const initialIndex = previewResources.findIndex((item) => item.index === resource.index);
    if (initialIndex === -1) return;
    const previewMediaList: PreviewMediaItem[] = previewResources.map((item) => ({
      url: item.url,
      cover: item.coverUrl,
      width: item.width,
      height: item.height,
      type: item.mediaType === 'video' ? 'video' : 'image',
      task_id: '',
      name: item.name,
      asset_id: item.asset_id ? `${item.asset_id}` : undefined,
    }));
    openPreview({
      previewImageList: previewMediaList,
      initialIndex,
      previewShowDownload: false,
    });
  }, [inputComposer.resourceList]);

  /** 关闭 IP 详情弹框并清空选中身份，避免下次误开旧 IP。 */
  const handleCloseIpDetail = useCallback(() => {
    setPreviewIpId(undefined);
  }, []);

  /** 上报 Skill 弹框内容曝光，按图中规范补齐 tab、skill 和位置参数。 */
  const trackSkillShow = useCallback((params: SkillTrackParams) => {
    if (!params.skill) return;
    mtstatTrack('roboneo_input_skills_show', {
      tab_id: `${params.tab.id}`,
      tab_name: params.tab.name,
      skill_id: `${params.skill.id}`,
      skill_name: params.skill.name,
      location: trackLocation,
    });
  }, [trackLocation]);

  /** 上报 Skill 弹框内容点击，选择和取消选择都按实际点击内容记录。 */
  const trackSkillClick = useCallback((params: SkillTrackParams) => {
    if (!params.skill) return;
    mtstatTrack('roboneo_input_skills_click', {
      tab_id: `${params.tab.id}`,
      tab_name: params.tab.name,
      skill_id: `${params.skill.id}`,
      skill_name: params.skill.name,
      location: trackLocation,
    });
  }, [trackLocation]);

  /** 上报 Skill 弹框分类 tab 点击，点击一次上报一次。 */
  const trackSkillTabClick = useCallback((params: SkillTrackParams) => {
    mtstatTrack('roboneo_input_skills_tab_click', {
      tab_id: `${params.tab.id}`,
      tab_name: params.tab.name,
      location: trackLocation,
    });
  }, [trackLocation]);

  /** 提交时记录 roboneo_team 入口并绑定角色，建房成功后携带真实房间 ID 上报发送。 */
  const submitPrompt = useCallback(async () => {
    if (isSubmitDisabled) return;
    const ipIds = inputComposer.resourceList.flatMap((item) => (
      item.mediaType === 'input_ip' && item.ipId ? [item.ipId] : []
    ));
    let progress: AgentTeamRoomProgress = {};
    /** 角色已绑定到项目后交接给目标画布，复用其他角色入口的初始化流程。 */
    const navigateCreatedRoom = async (target: AgentTeamRoomResult) => {
      if (target.projectId && target.roomId && roleCanvasScopeKey) {
        rememberMyIpProjectHandoff(target.projectId, target.roomId, ipIds, roleCanvasScopeKey, target.canvasId);
      }
      useOfficialStore.getState().navigateMap.historyTeamFlow({
        room_id: target.roomId,
        project_id: target.projectId,
        canvas_id: target.canvasId,
        initial_ip_id: ipIds[0],
      });
      return true;
    };
    const room = await createRoom({
      roomCreateParams: { extra: { entry_type: 'roboneo_team' } },
      prompt: inputComposer.inputText,
      markElementList: inputComposer.resourceList,
      ...(ipIds.length ? { ip_ids: ipIds } : {}),
      ...(ipIds.length && roleCanvasScopeKey ? {
        completion: {
          getProgress: () => progress,
          onProgress: (next: AgentTeamRoomProgress) => { progress = { ...next }; },
          navigate: navigateCreatedRoom,
        },
      } : {}),
      agentInfoList: inputComposer.agentInfoList,
      usedModel: inputComposer.usedModel,
      activeMode: inputComposer.activeMode,
      agentThinkingModeType: inputComposer.agentThinkingModeType,
      trackLocation: createTrackLocation ?? trackLocation,
      isAutoSubmit: true,
      skillList: selectedSkillList.length ? selectedSkillList : undefined,
      autoPrefixUnreferencedResources: false,
    });
    if (!room) return;
    mtstatTrack('command_send_click', {
      room_id: room.roomId,
      prompt_text: inputComposer.inputText,
      message_type: [
        inputComposer.images.length && 'image',
        inputComposer.resourceList.some((item) => item.mediaType === 'input_ip') && 'ip',
        inputComposer.videos.length && 'video',
        inputComposer.inputText && 'prompt',
        inputComposer.documents.length && 'file',
      ].filter(Boolean).join(','),
      image_num: inputComposer.imageCount,
      video_num: inputComposer.videos.length,
      file_num: inputComposer.documents.length,
      url: inputComposer.resourceList.map((item) => item.url).join(','),
      agent_id: inputComposer.agentInfoList.map((item) => item.id).join(','),
      ip_id_list: ipIds.join(','),
      content_from: inputComposer.resourceList.map((item) => (item.mediaType === 'input_ip' ? 'ip_role' : item.source)).filter(Boolean).join(','),
      input_model_name: `${inputComposer.usedModel.imageModel.modelName || 'auto'},${inputComposer.usedModel.videoModel.modelName || 'auto'}`,
      input_model_value: `${inputComposer.usedModel.imageModel.modelValue || 'auto'},${inputComposer.usedModel.videoModel.modelValue || 'auto'}`,
      mode: 'agent_teams',
      thinking_mode: inputComposer.agentThinkingModeType,
      skill_id: selectedSkillList.map((item) => item.id).join(','),
      asset_num: inputComposer.resourceList.filter((item) => item.source === 'asset').length,
      location: trackLocation,
    });
  }, [
    createTrackLocation,
    isSubmitDisabled,
    inputComposer.images.length,
    inputComposer.imageCount,
    inputComposer.videos.length,
    inputComposer.documents.length,
    inputComposer.resourceList,
    inputComposer.agentInfoList,
    inputComposer.inputText,
    inputComposer.usedModel,
    inputComposer.activeMode,
    inputComposer.agentThinkingModeType,
    roleCanvasScopeKey,
    selectedSkillList,
    trackLocation,
    createRoom,
  ]);

  return (
    <>
      <BorderBeam
        size={isInputPanelActive ? 'line' : 'md'}
        colorVariant="ocean"
        duration={isInputPanelActive ? 2.4 : 1.96}
        strength={0.7}
      >
        <div
          ref={inputPanelRef}
          className={classNames(styles.homeInputPanel, { [styles.homeHero]: showExtendedSelectors }, className)}
          data-node-id="36984:127540"
        >
        <input
          ref={localUploadInputRef}
          type="file"
          accept={localUploadAccept}
          multiple
          hidden
          disabled={inputComposer.uploading || !localUploadAccept}
          onChange={handleLocalUploadChange}
        />
        {
        shouldShowResourceInput ? (
          <div className={styles.previewArea}>
            <MediaInput
              className={classNames(styles.mediaInput)}
              previews={inputComposer.resourceList}
              onRemove={inputComposer.actions.removeResource}
              onItemClick={handlePreviewResource}
              skillList={showExtendedSelectors ? undefined : selectedSkillList}
              onSkillRemove={inputComposer.actions.removeSelectedSkill}
              isLoading={inputComposer.uploading}
              loadingCount={inputComposer.uploadingCount}
            />
          </div>
        ) : null
      }
        <div className={styles.inputTextBox}>
          <InputTextArea
            ref={inputComposer.actions.inputRef}
            className={styles.textarea}
            textareaClassName={showExtendedSelectors ? styles.heroEditor : undefined}
            placeholderClassName={showExtendedSelectors ? styles.heroPlaceholder : undefined}
            placeholder={inputPlaceholder}
            initialValue={inputComposer.editorContent}
            atImageOptions={inputComposer.atImageOptions}
            atImagePopupPlacement="top"
            atImagePopupEmptyText={languageStore.smartcatTranslationText((v) => v.no_asset_to_reference)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onBridgeReady={(bridge) => {
              if (inputComposer.editorContent.length) {
                bridge.setValue(inputComposer.editorContent);
              }
            }}
            maxLength={5000}
            onChange={(params) => { inputComposer.actions.setEditorContent(params.value); }}
            onEnter={() => { if (!isSubmitDisabled) submitPrompt(); }}
          />
        </div>

        <InputPanelToolBar
          modelTrackContext={{ location: 'home_page_agent_input', roomId: '' }}
          roleTrackContext={{ location: 'home_page', roomId: '' }}
          uploadButtonList={trackedUploadButtonList}
          onUploadLimitToast={inputComposer.actions.showUploadLimitToast}
          uploadPopoverOpen={inputComposer.uploadPopoverOpen}
          onUploadPopoverOpenChange={inputComposer.actions.setUploadPopoverOpen}
          showModelSelect={showExtendedSelectors && inputComposer.showModelSelect}
          modelMap={inputComposer.modelMap}
          usedModel={inputComposer.usedModel}
          setUsedModel={inputComposer.actions.setUsedModel}
          onModelRemove={inputComposer.actions.resetUsedModel}
          showSkillSelect={inputComposer.showSkillSelect}
          skillCategories={inputComposer.skillCategories}
          skillList={inputComposer.skillList}
          selectedSkill={inputComposer.selectedSkillInfo}
          onSkillSelect={inputComposer.actions.setSelectedSkill}
          showSelectionNamesInTrigger={showExtendedSelectors}
          showRoleSelect={showExtendedSelectors}
          roleSelectContent={roleSelectContent}
          roleList={inputComposer.roleList}
          selectedRoles={inputComposer.selectedRoleInfoList}
          roleSelectDisabled={inputComposer.imageUploadDisabled}
          onRoleSelect={inputComposer.actions.setSelectedRoles}
          onRolePopupOpenChange={onRolePopupOpenChange}
          onSkillShow={trackSkillShow}
          onSkillClick={trackSkillClick}
          onSkillTabClick={trackSkillTabClick}
          isSubmitDisabled={isSubmitDisabled}
          onSubmit={submitPrompt}
          assetLibraryVisible={inputComposer.assetLibraryVisible}
          onCloseAssetLibrary={inputComposer.actions.closeAssetLibrary}
          renderAssetModalContent={(props) => {
            if (resourcePopupType === 'assetLibrary') {
              return React.createElement(
                'div',
                { className: styles.assetLibraryDialog },
                React.createElement(AssetLibraryModalContent, {
                  ...props,
                  // 首页打开完整资产库，媒体筛选不应隐藏独立的角色 IP 列表。
                  enableIpCategory: true,
                  browseIpCategory: true,
                  insertTarget: 'home_page',
                  pickerSource: 'subjectLibrary',
                  maxSelectableCount: assetLibraryMaxSelectableCount,
                  maxIpSelectableCount: remainingImageCount,
                  onIpItemsAdd: handleIpItemsAdd,
                  batchConfirmLabel: assetLibraryAddLabel,
                  assetActionLabel: assetLibraryAddLabel,
                  enableAddCanvasClickTrack: false,
                  allowedMaterialTypes: HOME_ASSET_LIBRARY_ALLOWED_TYPES,
                }),
              );
            }
            return React.createElement(HistoryModalContent, props);
          }}
          onAssetItemClick={(item) => { inputComposer.actions.addAssetResources([item]); }}
          onAssetBatchConfirm={(items) => { inputComposer.actions.addAssetResources(items); }}
          trackLocation={assetTrackLocation}
          renderSubmitButton={renderSubmitButton
            ? () => renderSubmitButton({
              disabled: isSubmitDisabled,
              onClick: submitPrompt,
            })
            : undefined}
        />
      </div>
      </BorderBeam>
      {previewIpId ? <MyIpDetailModal ipId={previewIpId} onClose={handleCloseIpDetail} /> : null}
    </>
  );
}

export default React.memo(HomeInputPanel);
